import { type MouseEvent } from "react";
import { Col, Row } from "../../../common/components/Layout";
import NullableImage from "../../../common/components/NullableImage";
import ProductDesignation from "../ProductDesignation";
import {
  AliasesParameter,
  CountryOfOriginDescriptorParam,
  GPSDParameter,
  OldDesignationParameter,
  SchaefflerIdParameter,
  TariffParameter,
} from "../../../common/components/Product/ProductParams";
import { MAX_QUANTITY } from "../../../utils/constants";
import { type PdpPageProductData } from "../../models/PdpPageProductData";
import { Permission, userHasPermission } from "../../../utils/permissions";
import { userIsCustomer, userIsPublic, userIsRegistered } from "../../../utils/roles";
import { ImageFormat } from "../../../dto/ProductDataDTO";
import { ContactSalesTeamLink, PreferredProductsLink } from "./common/PdpLinks";
import CircleSpinner from "../../../common/components/CircleSpinner/CircleSpinner";
import PdpPrice from "../PdpPrice";
import { RebateLabelType } from "../../../common/components/Price/v1/Price";
import { Trans, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { type PriceData } from "../../../models/PriceData";

import { PriceReferenceParam } from "~/feature/cart/cart/components/LineItemEditableParams";
import OptimeSolutionsMessage from "../../../common/components/Messages/OptimeSolutionsMessage";
import { QuantityInput } from "../../../common/components/QuantityInput/QuantityInput";
import { StockLevelSection } from "../AddToCartCard/StockLevelSection";
import { trackPdpClick, trackPdpWishlistClick } from "../../../feature/Analytics/ga/ga";
import { mapOptionalParts4Wishlist } from "../../../Wishlist/modal/AddToWishlist/utils";
import { getListPrice, isProductPMA } from "../../../feature/ProductDescriptor/v1/productDescriptorUtils";
import { usePackageRestrictionContext } from "../../../common/components/PackageRestrictionContext/PackageRestrictionContext";
import {
  isProductComparisonCheckboxVisible,
  ProductComparisonCheckbox,
} from "../../../ProductComparison/ProductComparisonCheckbox";
import { NewVersionMessage } from "../../../common/components/Messages/NewVersionMessage";
import { AddToCartService } from "../../../AddToCart/AddToCartService";
import { isKITProduct, type PackagingInformationEntry } from "../../../models/ProductData";
import { canSeeAddToCartButton, canSeeListPrice } from "../../../utils/features";
import { HousingMessages } from "../../../common/components/Messages/HousingMessages";
import { type AddToCartFormData, type OptionalPartFormData } from "../../../AddToCart/hooks/useAddToCartNew";
import { useFormContext } from "react-hook-form";
import { multiply, volatileSum, zero } from "../../../utils/PriceUtils";
import { openAddToWishlistWizard } from "../../../Wishlist/modal/AddToWishlist/AddToWishlistService";
import { usePdpSummaryLogic } from "~/ProductDetails/components/ProductSummary/common/usePdpSummaryLogic";
import { getIsContractAvailable, isSpecialPrice } from "~/ProductDetails/redux/productDetailsSelectors";
import { getPrimaryImage } from "~/utils/product/getPrimaryImage";
import { InfoProductMessagePMA } from "~/ProductDetails/components/ProductSummary/InfoProductMessagePMA";
import { getRedirectToRegisterPage, redirectToUrl } from "~/utils/url/urlUtils";
import { KitPrices } from "~/ProductDetails/components/ProductSummary/components/KitPrices";
import { Alert } from "~/common/m3/Alert/Alert";
import { InfoOrderHandlingUnitQuantity } from "./InfoOrderHandlingUnitQuantity";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";
import { Button, Stack, styled, Typography } from "@mui/material";
import { PredefinedTraits } from "~/ProductDetails/components/ProductTrait";
import { getPurchaseDataFromUrl, selectAmountInCart } from "./PdpSummaryHelper";
import { usePurchasePlace } from "~/feature/Analytics/ga/recommendations/RecommendationContexts";
import { renderButtons } from "~/ProductDetails/components/ProductSummary/components/Buttons/usePdpButtons";
import { TabId } from "~/ProductDetails/components/usePDPTabs";
import RichTooltip from "~/common/components/RichTooltip/RichTooltip";
import Cycle from "~/common/icons/Cycle";
import parse from "html-react-parser";
import { IncludesSurchargeDisclaimer } from "~/common/components/Messages/IncludesSurchargeDisclaimer";
import { isTariffSurchargeEnabled } from "~/feature/cart/cart/utils/cartUtils";
import ProductMMD from "~/feature/Product/elements/ProductMMD";
import MMDAssistant from "../MMDAssistant/MMDAssistant";
import { useDisplayMMDAssistant } from "~/ProductDetails/context/DisplayMMDAssistantContext";
import { useSalesHelper } from "~/SalesCampaign/salesHelper";
import { PriceElements } from "~/common/components/Price/PriceV4";
import { URL_RECONDITIONABLE } from "~/utils/url/staticUrls";
import Tag from "~/common/components/Tag/Tag";
import { usePdpCmnLogic } from "../MMDAssistant/logic/usePdpCmnLogic";
import { Product } from "~/feature/Product";
import { ProductDescriptorContext } from "~/feature/Product/ProductContext";

interface Props {
  productDescriptor: ProductDescriptor<PdpPageProductData>;
  hasPreferredProducts: boolean;
  changeTab: (tabId: string) => void;
}

const isSalesman = userHasPermission(Permission.ADD_TO_QUOTE);
const isPublic = userIsPublic() || userIsRegistered();

/*
 * Product Details page summary (starting from breadcrumbs, finishing before tabs) for all other cases.
 * Non-salesman, non-distributor - meaning it can be OEM purchaser, registered, anonymous (and others)
 */
export const MobilePdpSummary = ({ productDescriptor, hasPreferredProducts, changeTab }: Props) => {
  const { t } = useTranslation();
  const reco_place = usePurchasePlace();

  const displayMMDAssistant = useDisplayMMDAssistant();

  const formProps = useFormContext<AddToCartFormData>();
  const quantity = formProps.watch("quantity");
  const packCode = formProps.watch("selectedPackCode");
  const priceReference = formProps.watch("priceReference");
  const pack: PackagingInformationEntry | null = formProps.watch("selectedPack");
  const price = formProps.watch("netPrice");

  const isOnSale = useSalesHelper(productDescriptor).isPackageOnSale(packCode);
  const saleActive = isOnSale && (formProps?.watch("salePriceEnabled") ?? false);

  const optionalParts = formProps.watch("optionalParts") as Dict<OptionalPartFormData>;

  const qtyInCart = useSelector(
    selectAmountInCart({
      product: productDescriptor,
      packCode,
    }),
  );

  const {
    buttons: { showWishlistButton },
    flags: { showBetterDeal, showManufacturerLogo },
  } = usePdpSummaryLogic({
    product: productDescriptor,
    qtyInCart,
    quantity,
    packCode: "",
  });

  const isContractProduct = getIsContractAvailable(productDescriptor);

  const showCartButton = canSeeAddToCartButton(true);

  const webservicesDisabled = window.SCHA.config.disableAllWS;
  const { isLoading, availability: availabilityInfo } = productDescriptor;
  const isError = productDescriptor.isError && !webservicesDisabled;
  const product = productDescriptor.base;
  const isOptime = product.isOptime;
  const isPMA = isProductPMA(productDescriptor);
  const isKit = isKITProduct(product);
  const quantityVisible = product.quantityVisible && !product.externalProduct;
  const isExternalProduct = product.externalProduct;

  const tariffSurcharge = isTariffSurchargeEnabled() && formProps.watch("tariffSurcharge");

  // TODO: What the hell can I do with this...
  const priceInfo = productDescriptor.prices[0];
  const isPriceAt15Digit = productDescriptor.isMultiPrice;

  const isContractItem = getIsContractAvailable(productDescriptor);

  let isAddToCartEnabled;
  if (isContractItem) {
    isAddToCartEnabled = userHasPermission(Permission.VIEW_CONTRACT) && userHasPermission(Permission.ADD_TO_CART);
  } else if (!isPriceAt15Digit) {
    isAddToCartEnabled = priceInfo && priceInfo.hasPrice && availabilityInfo && availabilityInfo.hasPackaging;
  } else {
    isAddToCartEnabled = availabilityInfo && availabilityInfo.hasPackaging;
  }

  const qtyInputEnabled = !isLoading && ((showCartButton && !isError) || showWishlistButton) && product.quantityVisible;

  let addToCartDisabled = webservicesDisabled || !isAddToCartEnabled;
  if (isPMA) {
    addToCartDisabled = false;
  }
  if (!userIsCustomer()) {
    addToCartDisabled = false;
  }
  if (webservicesDisabled) {
    addToCartDisabled = true;
  }

  const priceUnit = priceInfo ? priceInfo.priceUnit : 1;

  const primaryImage = getPrimaryImage(product.images, { imageFormat: ImageFormat.product });

  const { manufacturerLogoUrl, brandLogoUrl } = product;

  const restrictedQuery = usePackageRestrictionContext();
  const openAddToCartModal = (e?: MouseEvent<HTMLElement>) => {
    if (e) {
      e.preventDefault();
    }
    const { recommendationPlace, recommendationType } = getPurchaseDataFromUrl();
    const context = {
      page: reco_place,
      recommendationPlace,
      recommendationType,
    };
    AddToCartService.openAddToCartWizard(
      {
        productCode: product.code,
        restrictionQuery: restrictedQuery ?? "",
        initialData: formProps.getValues(),
        source: "pdp",
      },
      context,
    );
  };

  const checkAvailabilityClick = () => {
    trackPdpClick("Check availability details", productDescriptor.base.designation);
    openAddToCartModal();
  };

  const selectClick = () => {
    trackPdpClick("Select Product", productDescriptor.base.designation);
    openAddToCartModal();
  };

  const openModalWishlist = (e?: MouseEvent<HTMLElement>) => {
    if (e) {
      e.preventDefault();
    }
    if (userIsPublic()) {
      redirectToUrl(getRedirectToRegisterPage({ openLogin: true }));
    } else {
      trackPdpWishlistClick(productDescriptor.base.designation, 1);

      openAddToWishlistWizard({
        productCode: product.code,
        initialQuantity: quantity,
        initialPriceReference: priceReference,
        addTracking: (product) => trackPdpWishlistClick(product.base.designation, 2),
        selectedOptionalParts: mapOptionalParts4Wishlist(productDescriptor.base, optionalParts),
      });
    }
  };

  const hasSurcharge = formProps.watch("surcharge") > 0;

  const canSeePrice = userHasPermission(Permission.PRICE);

  const isHousingKit = isKITProduct(productDescriptor.base);

  const showStockSection = !isPublic && !isHousingKit;

  const selectedOptionalParts = Object.values(optionalParts).filter((item) => item.selected);

  const countTotal = (price: PriceData | null, priceUnit: number, qty: number) => {
    return !price ? null : multiply(price, qty / priceUnit);
  };
  const optionalPartsPrice = volatileSum(
    ...selectedOptionalParts.map((item) => countTotal(item.netPrice, item.priceUnit, item.quantity) ?? zero()),
  );

  const optionalPartsHaveSurcharge =
    selectedOptionalParts.length > 0 && selectedOptionalParts.some((item) => item.surcharge > 0);

  const totalPrice = countTotal(price, priceUnit, quantity);
  const isSpecial = isSpecialPrice(productDescriptor, "");

  const buttons = renderButtons(productDescriptor, {
    atc: {
      disabled: addToCartDisabled,
      onClick: selectClick,
    },
    wishlist: {
      onClick: openModalWishlist,
    },
    configurator: {
      onClick: () => changeTab(TabId.CONFIGURATOR),
    },
    atcFromSP: {
      quantity,
    },
  });

  const listPrice = getListPrice({ product: productDescriptor, packCode });
  const showListPrice =
    !saleActive &&
    !!listPrice &&
    canSeeListPrice({
      product: productDescriptor,
      isSpecialPrice: price?.isSpecial ?? false,
      hasSurcharge: (price?.surcharge ?? 0) > 0,
    });

  const mmdCmnData = usePdpCmnLogic();

  return (
    <ProductDescriptorContext value={productDescriptor}>
      <div className={"scha__pdp__product-box"}>
        <Stack gap={2}>
          {displayMMDAssistant && (
            <MMDAssistant
              marketDesignation={product.marketDesignation}
              initialData={{
                quantity: quantity,
                priceReference: priceReference,
                ...mmdCmnData,
              }}
            />
          )}

          <Stack gap={{ phone: 2, tablet: 3 }}>
            <Stack gap={2}>
              <div className={"scha__pdp-mobile__product-image-container"}>
                <Row>
                  <Col size={10}>{primaryImage && <NullableImage data={primaryImage} />}</Col>
                  <Col size={2} className="scha__product-image__logos">
                    {showManufacturerLogo && (
                      <img className={"scha__pdp__manufacturer-logo"} src={manufacturerLogoUrl} />
                    )}
                    {brandLogoUrl && <img className={"scha__pdp__brand-logo"} src={brandLogoUrl} />}
                  </Col>
                </Row>
              </div>

              {isKit && <HousingMessages.NotFinal />}
            </Stack>

            <Stack gap={2}>
              <ProductDesignation
                as={"h1"}
                className={"scha__pdp__designation"}
                designation={product.designation}
                size={"headline-medium"}
                isPreferred={product.preferred}
              />

              {product.marketDesignation && <ProductMMD marketDesignation={product.marketDesignation} />}

              <Stack gap={1}>
                <Product.ShortDescriptionV2
                  component={"h2"}
                  variant="titleMedium"
                  data-test-id={"pdp-short-description"}
                />
                <Product.DescriptionV2 component={"h3"} variant="titleSmall" data-test-id={"pdp-long-description"} />
              </Stack>
              {productDescriptor.base.reconditionable && (
                <RichTooltip
                  content={
                    <Typography>
                      {parse(t("cmsnavigationnode.schaefflerContentCatalog.reconditionableInfo"))}
                    </Typography>
                  }
                  actions={[
                    <Button href={URL_RECONDITIONABLE} variant="text">
                      {t("cmsnavigationnode.schaefflerContentCatalog.moreInfo")}
                    </Button>,
                  ]}
                >
                  <Tag icon={<Cycle fontSize="small" />} iconPosition="start" variant="success">
                    {t("cmsnavigationnode.schaefflerContentCatalog.reconditionable")}
                  </Tag>
                </RichTooltip>
              )}
            </Stack>
          </Stack>

          {/* Removed for non salesman customers as of SCHAEFF-24168 */}
          <NewVersionMessage baseProduct={product} />

          {isError && t("hybrisAjaxError")}

          {isLoading && <CircleSpinner />}

          {userIsCustomer() && !isLoading && (
            <>
              {canSeePrice && (
                <>
                  {isPMA ? null : isHousingKit && totalPrice ? (
                    <KitPrices
                      className={"mb-40"}
                      product={productDescriptor.base}
                      totalPrice={totalPrice}
                      optionalPartsPrice={optionalPartsPrice}
                      includesSurcharge={hasSurcharge}
                      optionalPartsIncludesSurcharge={optionalPartsHaveSurcharge}
                    />
                  ) : (
                    <PdpPrice
                      price={price}
                      isMultiPrice={isPriceAt15Digit}
                      priceUnit={priceUnit}
                      isSpecialPrice={isSpecial}
                      isMonthlySubsription={!quantityVisible}
                      includesSurcharge={hasSurcharge}
                      tariffSurcharge={tariffSurcharge}
                      product={productDescriptor}
                      quantity={quantity}
                      rebateVariant={RebateLabelType.Default}
                    />
                  )}
                </>
              )}

              {showListPrice && (
                <PriceElements.Stack gap={0}>
                  <PriceElements.Price price={listPrice} variant="titleMedium" />
                  <PriceElements.ListPriceLabel unit={price?.unit ?? 1} variant="labelLarge" color="textSecondary" />
                </PriceElements.Stack>
              )}
              {isPriceAt15Digit && (
                <Alert severity={"info"}>
                  <Trans i18nKey={"productdetail.multipleprices.purchaser"}>
                    Multiple prices possible, please
                    <a href={"#"} onClick={openAddToCartModal}>
                      select package option
                    </a>
                  </Trans>
                </Alert>
              )}
            </>
          )}

          <ParamGrid>
            <OldDesignationParameter product={product} label={t("productDetailPage.oldDesignation")} />
            <SchaefflerIdParameter productDescriptor={productDescriptor} />
            <AliasesParameter product={product} mode={isSalesman ? "13-digits" : "all"} />
            <TariffParameter product={product} />
            <CountryOfOriginDescriptorParam descriptor={productDescriptor} />
            <GPSDParameter product={product} />
          </ParamGrid>

          <Stack gap={1}>
            {product.preferred && <PredefinedTraits.PreferredProduct />}
            {isContractProduct && <PredefinedTraits.ContractProduct />}
            {/*<PredefinedTraits.BoughtBefore/>*/}
          </Stack>

          {!isExternalProduct && (
            <PriceReferenceParam
              data-test-id={"pdp-price-reference"}
              priceReference={priceReference}
              onChange={(value) => formProps.setValue("priceReference", value ?? "")}
              product={productDescriptor.base}
              editable={qtyInputEnabled}
              externalLabel
            />
          )}

          {isOptime && <OptimeSolutionsMessage />}

          {quantityVisible && (
            <Stack gap={1}>
              <QuantityInput
                min={1}
                data-test-id={"pdp-input-quantity"}
                max={MAX_QUANTITY}
                value={quantity}
                disabled={!qtyInputEnabled}
                step={1}
                variant={"compact"}
                onValueChange={(newVal) =>
                  formProps.setValue("quantity", newVal, { shouldTouch: true, shouldDirty: true })
                }
              />
              <InfoOrderHandlingUnitQuantity
                preferredPackQuantity={pack?.preferredPackQuantity ?? null}
                packSize={pack?.packSize ?? 1}
                quantity={quantity}
              />
            </Stack>
          )}

          {showBetterDeal && <Alert severity="info">{t("addProduct.scalingDeal")}</Alert>}

          {showStockSection && (
            <StockLevelSection
              productDescriptor={productDescriptor}
              quantity={quantity}
              onCheckAvailabilityClick={checkAvailabilityClick}
              checkAvailabilityDisabled={showCartButton ? addToCartDisabled : false}
              showReplenishment={false}
            />
          )}

          {!isLoading && showCartButton && canSeePrice && productDescriptor.isError && (
            <Alert severity="error">{t("addProduct.wsError")}</Alert>
          )}

          {hasPreferredProducts && <PreferredProductsLink baseType={product.preferredProductsFamilyName} />}
          {isProductComparisonCheckboxVisible(product) && !isExternalProduct && (
            <ProductComparisonCheckbox productCode={productDescriptor.base.code} gaLocation={"Product Detail Page"} />
          )}
          {isPMA && userIsCustomer() && <InfoProductMessagePMA />}

          <Stack gap={1}>{buttons}</Stack>

          <ContactSalesTeamLink product={productDescriptor.base} />

          {!tariffSurcharge && <IncludesSurchargeDisclaimer hasSurcharge={hasSurcharge} tariffSurcharge={false} />}
        </Stack>
      </div>
    </ProductDescriptorContext>
  );
};

const ParamGrid = styled("div")({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "16px",
});
